import React, { useCallback } from 'react';
import { DateUtils } from 'react-day-picker';
import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import GenericCalendar, { CalendarType } from '@rover/components/src/datetime/GenericCalendar';
import { getMiddleDays } from '@rover/components/src/datetime/MultiDateAutoFillPicker/MultiDateAutoFillPickerGeneric';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { SERVICE_SELECTION_OPTIONS } from '@rover/constants/src/translations/serviceSelector.constants';
import { Box } from '@rover/kibble/core';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import { DayTimeAvailability } from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Dates';
import { shouldShowControls } from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import useBrandSpecificPets from '@rover/shared/js/hooks/useBrandSpecificPets';
import useFlag, { FLAGS } from '@rover/shared/js/hooks/useFlag';
import OneTimeRepeatToggleButtons from '@rover/shared/js/search/components/OneTimeRepeatToggleButtons';
import RepeatServiceScheduler from '@rover/shared/js/search/components/RepeatServiceScheduler';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { AvailabilitySearchFilters } from '@rover/types/src/AvailabilitySearchFilters';
import type { Day } from '@rover/types/src/datetime/Day';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import { ServiceFrequencyEnum } from '@rover/types/src/ServiceFrequency';
import { isCatOnlyAvailablePetTypesBasedOnPetTypes } from '@rover/utilities/getAvailablePetTypes/availablePetTypesUtils';

import { SpacesRequired } from '../../Filters';
import { SearchInterstitialSelectDates } from '../../Filters/Collections/Wizard/analyticsEvents';
import { makeDayFromDate } from '../utils';

export type DateAvailabilitySectionProps = {
  searchFilters: SearchFilters;
  locale: Locale;
  onFiltersChange: (filters: Partial<SearchFilters>) => void;
  serviceType: ServiceType;
  serviceTypeFilterOptions: SearchFiltersOptions;
};

const getDatePlaceholders = (
  service: ServiceType,
  frequency: ServiceFrequencyEnum | undefined,
  i18n: I18n
) => {
  const isBoarding = service === ServiceType.OVERNIGHT_BOARDING;
  const isHouseSitting = service === ServiceType.OVERNIGHT_TRAVELING;

  let datePlaceholders = { start: i18n._(msg`Start`), end: i18n._(msg`End`) };
  if (isBoarding) {
    datePlaceholders = { start: i18n._(msg`Drop off`), end: i18n._(msg`Pick up`) };
  }
  if (frequency === ServiceFrequencyEnum.REPEAT || isHouseSitting) {
    datePlaceholders = { start: i18n._(msg`Start date`), end: i18n._(msg`End date`) };
  }
  return datePlaceholders;
};

const DateAvailabilitySection = ({
  searchFilters,
  locale,
  onFiltersChange,
  serviceType,
  serviceTypeFilterOptions,
}: DateAvailabilitySectionProps): JSX.Element => {
  const { i18n } = useLingui();
  const { DF } = useI18nContext();
  const brandAvailablePets = useBrandSpecificPets();
  const isCatOnlyBrand = isCatOnlyAvailablePetTypesBasedOnPetTypes(brandAvailablePets);
  const { frequency, startDate, endDate, selectedDays } = searchFilters;

  const isFiltersM2 = useFlag(FLAGS.ROLLOUT_MOBILE_FILTERS_M2);

  const isOvernightService =
    serviceType === ServiceType.OVERNIGHT_BOARDING ||
    serviceType === ServiceType.OVERNIGHT_TRAVELING;

  const handleFrequencyChange = useCallback(
    (serviceFrequency?: ServiceFrequencyEnum) => {
      const shouldResetDate = serviceFrequency === ServiceFrequencyEnum.REPEAT;
      const shouldResetDays = serviceFrequency === ServiceFrequencyEnum.ONE_TIME;
      onFiltersChange({
        frequency: serviceFrequency,
        ...(shouldResetDate && { endDate: undefined }),
        ...(shouldResetDays && { selectedDays: [] }),
      });
    },
    [onFiltersChange]
  );

  const handleOneTimeDateChange = useCallback(
    (days: Day[]) => {
      const newStartDate = days[0]?.date ?? null;
      const newEndDate = days.length > 1 ? days[days.length - 1]?.date ?? null : null;
      onFiltersChange({ startDate: newStartDate, endDate: newEndDate });
      emitAnalyticsEvent(new SearchInterstitialSelectDates());
    },
    [onFiltersChange]
  );

  const handleRepeatStartDateChange = useCallback(
    (selectedStartDate?: Date | null) => {
      const hasValidDates = endDate && selectedStartDate;
      const isStartAfterEnd = hasValidDates && DateUtils.isDayAfter(selectedStartDate, endDate);
      const updatedEndDate = hasValidDates && !isStartAfterEnd ? endDate : undefined;
      onFiltersChange({ startDate: selectedStartDate, endDate: updatedEndDate });
      emitAnalyticsEvent(new SearchInterstitialSelectDates());
    },
    [endDate, onFiltersChange]
  );

  const handleSelectedDaysChange = (days: Day[]): void => {
    onFiltersChange({ selectedDays: days });
  };

  const handleAvailabilityChange = (availabilityFilters: AvailabilitySearchFilters) => {
    onFiltersChange(availabilityFilters);
  };

  const minDate = React.useMemo(() => new Date(), []);

  const getSelectedDays = useCallback((): Day[] => {
    if (!startDate) return [];

    if (endDate) {
      const startDay = makeDayFromDate(startDate, DF);
      const endDay = makeDayFromDate(endDate, DF);
      return getMiddleDays(startDay, endDay, DF);
    }

    return [makeDayFromDate(startDate, DF)];
  }, [startDate, endDate, DF]);

  const { morningAvailability, middayAvailability, eveningAvailability } = serviceTypeFilterOptions;

  const { showDaytimeAvailability, showSpacesRequired } = shouldShowControls({
    serviceTypeSlug: serviceType,
    serviceOptions: serviceTypeFilterOptions,
    isCatOnly: isCatOnlyBrand,
  });

  const datePlaceholders = getDatePlaceholders(serviceType, frequency, i18n);

  const selectedServiceType =
    SERVICE_SELECTION_OPTIONS(i18n).find((option) => option.value === serviceType)?.title || '';
  return (
    <Box pt="6x" pb="4x">
      {!isOvernightService && (
        <OneTimeRepeatToggleButtons
          frequency={frequency}
          label={i18n._(msg`Schedule`)}
          onChange={handleFrequencyChange}
        />
      )}
      {isOvernightService || frequency === ServiceFrequencyEnum.ONE_TIME ? (
        <Box mt={isOvernightService ? undefined : '4x'}>
          <GenericCalendar
            calendarType={CalendarType.Range}
            locale={locale}
            onChange={handleOneTimeDateChange}
            selectedDays={getSelectedDays()}
            isMobileBrowser
            serviceName={selectedServiceType}
            disableBeforeDateTime={minDate}
            newCalendarInteraction
            validationType="inline"
            showTwoInputs
            startDatePlaceholder={datePlaceholders.start}
            endDatePlaceholder={datePlaceholders.end}
            label={i18n._(msg`Dates`)}
          />
        </Box>
      ) : (
        <Box mt="4x">
          <RepeatServiceScheduler
            locale={locale}
            onSelectedDaysChange={handleSelectedDaysChange}
            onStartDateChange={handleRepeatStartDateChange}
            repeatStartDateLabel={i18n._(msg`Dates`)}
            selectedDays={selectedDays || []}
            showLabel
            serviceName={selectedServiceType}
            startDate={startDate}
            startPlaceholder={i18n._(msg`Start date`)}
            isMobileBrowser
            disableValidation
            rounded={isFiltersM2}
          />
        </Box>
      )}
      <Box mt="4x">
        {showDaytimeAvailability && (
          <DayTimeAvailability
            availabilityBuckets={{
              morning: morningAvailability?.label ?? null,
              midday: middayAvailability?.label ?? null,
              evening: eveningAvailability?.label ?? null,
            }}
            label={i18n._(msg`Which time(s) do you need?`)}
            labelIsVisible
            onChange={handleAvailabilityChange}
            searchFilters={searchFilters}
          />
        )}
        {showSpacesRequired && (
          <Box mt="4x">
            <SpacesRequired
              searchFilters={searchFilters}
              onChange={(spacesRequired) => onFiltersChange({ spacesRequired })}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DateAvailabilitySection;
